/**
 * Memory — what Vooice knows about people, facts, history.
 *
 * Four sub-tabs at /app/memory/{contacts,facts,audit,artifacts}.
 * Contacts + Facts are intentionally read-mostly; the design language
 * is "edit it like a paragraph" — the canonical edit path is asking
 * Vooice in Chat ("forget Lina"), not form fields here.
 */
import { useState } from "react";
import { NavLink, Link, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { audit, artifacts as artifactsApi } from "../api/client";
import { Card, Mono, Pill, Button } from "../components/v3";
import type { TrustZone } from "../api/types";

// ── Memory layout (sub-nav + outlet) ───────────────────────────────────────

export function Memory() {
  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto">
      <div className="mb-6">
        <Mono className="block mb-2">Memory</Mono>
        <h1 className="font-display text-3xl leading-tight">
          What she <span className="font-display-italic">remembers</span>.
        </h1>
        <p className="text-t2 mt-2 max-w-[52ch]">
          Names, facts, the audit trail, and artifacts she's produced. Edit
          memory by talking to her — say "forget that" or "Lina is at Bayhill,
          not Bayview".
        </p>
      </div>

      <SubNav />

      <div className="mt-5">
        <Routes>
          <Route index element={<Navigate to="contacts" replace />} />
          <Route path="contacts" element={<ContactsTab />} />
          <Route path="facts" element={<FactsTab />} />
          <Route path="audit" element={<AuditTab />} />
          <Route path="artifacts" element={<ArtifactsTab />} />
        </Routes>
        <Outlet />
      </div>
    </div>
  );
}

function SubNav() {
  const items = [
    { to: "/app/memory/contacts", label: "Contacts" },
    { to: "/app/memory/facts", label: "Facts" },
    { to: "/app/memory/audit", label: "Audit" },
    { to: "/app/memory/artifacts", label: "Artifacts" },
  ];
  return (
    <div className="flex gap-1 border-b border-line">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            [
              "relative px-4 py-2.5 text-sm font-medium transition-colors",
              isActive ? "text-t1" : "text-t3 hover:text-t1",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              {it.label}
              {isActive && (
                <span className="absolute bottom-[-1px] left-3 right-3 h-[2px] bg-amber rounded-t-sm" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

// ── Contacts tab ───────────────────────────────────────────────────────────

function ContactsTab() {
  // Backend endpoints for people.* are tool-only today; empty state is
  // the honest state until /api/memory/contacts ships.
  return (
    <div className="space-y-4">
      <SearchableEmpty
        eyebrow="Contacts · 0"
        title="She doesn't know anyone yet."
        body="Tell her in chat — 'add Sarah Johnson, +1 415 555 0177, my realtor.' She'll remember. Names, roles, allergies, partners, kids — edit it like a paragraph by saying so."
        cta="Open chat"
        ctaHref="/app/chat"
      />
      <ComingSoonRow
        title="Searchable contact list"
        body="Sortable table, group by role, export to CSV. Surfaces once /api/memory/contacts is wired."
      />
    </div>
  );
}

// ── Facts tab ──────────────────────────────────────────────────────────────

function FactsTab() {
  return (
    <div className="space-y-4">
      <SearchableEmpty
        eyebrow="Facts · 0"
        title="Nothing yet."
        body="Things Vooice remembers but didn't tie to a person — schedules, rules, preferences ('I don't take calls before 9 AM'). Add via chat."
        cta="Tell her something"
        ctaHref="/app/chat"
      />
      <ComingSoonRow
        title="Memory facts catalog"
        body="Sourced (which call/message taught her this), editable, forgettable. Surfaces once /api/memory/facts is wired."
      />
    </div>
  );
}

// ── Audit tab ──────────────────────────────────────────────────────────────

function AuditTab() {
  const [agentFilter, setAgentFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["audit", { agentFilter, actionFilter, page }],
    queryFn: () =>
      audit.list({
        agent_id: agentFilter || undefined,
        action: actionFilter || undefined,
        limit,
        offset: page * limit,
      }),
    refetchInterval: 10_000,
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <input
          type="text"
          value={agentFilter}
          onChange={(e) => {
            setAgentFilter(e.target.value);
            setPage(0);
          }}
          placeholder="Filter by agent ID…"
          className="px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 w-56 focus:outline-none focus:border-amber/40"
        />
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(0);
          }}
          className="px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 focus:outline-none focus:border-amber/40"
        >
          <option value="">All actions</option>
          <option value="tool.call">tool.call</option>
          <option value="agent.deploy">agent.deploy</option>
          <option value="auth.success">auth.success</option>
          <option value="auth.failure">auth.failure</option>
          <option value="sentinel.action">sentinel.action</option>
        </select>
        {data && <Mono className="ml-auto">{data.total} entries</Mono>}
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="grid grid-cols-[140px_140px_70px_1fr_90px_70px] gap-3 px-5 py-3 border-b border-line-soft">
          <Mono>Time</Mono>
          <Mono>Agent</Mono>
          <Mono>Zone</Mono>
          <Mono>Action</Mono>
          <Mono>Status</Mono>
          <Mono>Dur.</Mono>
        </div>

        {isLoading ? (
          <div className="p-5 space-y-2">
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
          </div>
        ) : !data?.entries || data.entries.length === 0 ? (
          <div className="p-12 text-center">
            <div className="font-display text-xl text-t2">No entries</div>
            <div className="text-t3 text-sm mt-2">
              Audit log is empty for these filters.
            </div>
          </div>
        ) : (
          data.entries.map((e) => (
            <AuditRow
              key={e.entry_id}
              entry={e}
              expanded={expanded === e.entry_id}
              onToggle={() =>
                setExpanded(expanded === e.entry_id ? null : e.entry_id)
              }
            />
          ))
        )}
      </Card>

      {data && data.total > limit && (
        <div className="flex items-center justify-between mt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            ← Previous
          </Button>
          <Mono>
            Page {page + 1} of {Math.ceil(data.total / limit)}
          </Mono>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={(page + 1) * limit >= data.total}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}

function AuditRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: any;
  expanded: boolean;
  onToggle: () => void;
}) {
  const statusTone =
    entry.result_status === "success"
      ? "ok"
      : entry.result_status === "denied"
      ? "rose"
      : "warn";
  return (
    <div className="border-b border-line-soft last:border-b-0">
      <button
        onClick={onToggle}
        className="grid grid-cols-[140px_140px_70px_1fr_90px_70px] gap-3 px-5 py-3 w-full text-left items-center hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-t3 text-xs font-mono">
          {new Date(entry.timestamp).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <span className="text-t1 font-mono text-xs truncate">
          {entry.agent_id}
        </span>
        <ZoneTag zone={entry.agent_zone as TrustZone} />
        <span className="text-t1 text-sm truncate">
          {entry.action}
          {entry.tool && (
            <span className="text-t3 ml-1">({entry.tool})</span>
          )}
        </span>
        <Pill tone={statusTone as any}>{entry.result_status}</Pill>
        <span className="text-t3 text-xs font-mono">
          {entry.duration_ms ? `${entry.duration_ms}ms` : "—"}
        </span>
      </button>
      {expanded && entry.details && (
        <pre className="px-5 py-3 bg-white/[0.02] text-xs text-t2 overflow-x-auto font-mono">
          {JSON.stringify(entry.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ZoneTag({ zone }: { zone: TrustZone }) {
  const tone = (
    {
      internal: "ok",
      bridge: "warn",
      external: "rose",
      system: "amber",
    } as const
  )[zone] ?? "neutral";
  return <Pill tone={tone}>{zone}</Pill>;
}

// ── Artifacts tab ──────────────────────────────────────────────────────────

function ArtifactsTab() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["artifacts"],
    queryFn: () => artifactsApi.list(),
  });

  // Client-side search filter — keeps the existing api signature.
  const filtered = data?.artifacts?.filter((a: any) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.title?.toLowerCase().includes(q) ||
      a.content?.toLowerCase().includes(q) ||
      a.category?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search artifacts…"
          className="flex-1 px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 focus:outline-none focus:border-amber/40"
        />
        {data && <Mono>{filtered?.length ?? 0} items</Mono>}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-32" />
          ))}
        </div>
      ) : !filtered || filtered.length === 0 ? (
        <Card padding="lg" className="text-center">
          <div className="font-display text-xl text-t2">No artifacts yet</div>
          <p className="text-t3 text-sm mt-2 max-w-[40ch] mx-auto">
            Artifacts are the things Vooice has built or saved on your behalf —
            documents, snippets, notes from calls. Ask her in chat to draft
            something and it'll appear here.
          </p>
          <div className="mt-4">
            <Button as="a" href="/app/chat" variant="amber" size="sm">
              Open chat
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a: any) => (
            <ArtifactCard key={a.id} artifact={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function ArtifactCard({ artifact }: { artifact: any }) {
  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-2">
        <Mono>{artifact.category ?? "Note"}</Mono>
        <Mono>{new Date(artifact.created_at).toLocaleDateString()}</Mono>
      </div>
      <h3 className="font-display text-lg leading-tight mb-2 line-clamp-2"
          style={{ fontVariationSettings: '"opsz" 36, "SOFT" 70' }}
      >
        {artifact.title || "Untitled"}
      </h3>
      <p className="text-t2 text-sm line-clamp-3 leading-relaxed">
        {artifact.content?.slice(0, 200) || "—"}
      </p>
    </Card>
  );
}

// ── Shared empty/coming-soon helpers ───────────────────────────────────────

function SearchableEmpty({
  eyebrow,
  title,
  body,
  cta,
  ctaHref,
}: {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  ctaHref: string;
}) {
  return (
    <Card padding="lg" glow>
      <Mono className="block mb-3">{eyebrow}</Mono>
      <h2 className="font-display text-2xl leading-tight mb-3 max-w-[24ch]">
        {title}
      </h2>
      <p className="text-t2 max-w-[52ch] leading-relaxed">{body}</p>
      <div className="mt-5">
        <Link to={ctaHref}>
          <Button variant="amber" size="sm">
            {cta}
          </Button>
        </Link>
      </div>
    </Card>
  );
}

function ComingSoonRow({ title, body }: { title: string; body: string }) {
  return (
    <Card padding="md" className="border-dashed">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium text-t1">{title}</h3>
          <p className="text-t3 text-sm mt-1 max-w-[60ch]">{body}</p>
        </div>
        <Pill tone="warn">Soon</Pill>
      </div>
    </Card>
  );
}
